import {
  Box,
  Drawer,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type {
  TransactionStatus,
  TransactionDirection,
} from "Infrastructure/Api/Api";
import { DateRangeFilter } from "Components/Shared/Filters/DateRangeFilter";
import { EmployeeFilter } from "Components/Shared/Filters/EmployeeFilter";
import { FilterActions } from "Components/Shared/Filters/FilterActions";
import type { FilterOption } from "Components/Shared/Filters/FilterOption";
import { PayPointFilter } from "Components/Shared/Filters/PayPointFilter";
import { RefundOrCancellationFilter } from "Components/Shared/Filters/RefundOrCancellationFilter";
import { TimePeriodFilter } from "Components/Shared/Filters/TimePeriodFilter";
import type {
  AvailableFilters,
  TimePeriod,
  TransactionFilters,
} from "Components/Transactions/hooks/useTransactionFilterManager";
import { Resources, useResource } from "Translations/Resources";
import { DirectionFilter } from "Components/Transactions/TransactionsSearchBar/TransactionFilters/DirectionFilter";
import { StatusFilter } from "Components/Transactions/TransactionsSearchBar/TransactionFilters/StatusFilter";

const RESOURCES = Resources.Components.Shared.Filters;

type Props = {
  isOpen: boolean;
  availableFilters: AvailableFilters;
  localFilters: TransactionFilters;
  onClose: () => void;
  onApply: () => void;
  onReset: () => void;
  onTimePeriodChange: (value: TimePeriod | undefined) => void;
  onPayPointChange: (option?: FilterOption) => void;
  onEmployeeChange: (option?: FilterOption) => void;
  onStatusChange: (value?: TransactionStatus) => void;
  onDirectionChange: (value?: TransactionDirection) => void;
  onRefundOrCancellationChange: (value: boolean) => void;
  onDateFromChange: (value?: Date) => void;
  onDateToChange: (value?: Date) => void;
};

export const TransactionFiltersDrawer = ({
  isOpen,
  availableFilters,
  localFilters: {
    timePeriod,
    payPoint,
    dateFrom,
    dateTo,
    employee,
    status,
    direction,
    isRefundOrCancellation,
  },
  onClose,
  onApply,
  onReset,
  onTimePeriodChange,
  onPayPointChange,
  onEmployeeChange,
  onStatusChange,
  onDirectionChange,
  onRefundOrCancellationChange,
  onDateFromChange,
  onDateToChange,
}: Props) => {
  const { t } = useResource();

  // Mirrors CardTransactionFiltersDrawer: a custom period with an incomplete date range would silently apply as
  // "no period filter" (determineDatesByPeriod returns dateFrom/dateTo undefined) while the chips still claim a
  // bounded range. Block Apply until both dates are set — but only when the date-range inputs are actually shown
  // (same condition as the DateRangeFilter below), so Apply can never get stuck disabled.
  const isApplyDisabled =
    !!availableFilters.timePeriod &&
    timePeriod === "custom" &&
    (!dateFrom || !dateTo);

  return (
    <Drawer
      anchor="bottom"
      open={isOpen}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: "80vh",
          },
        },
      }}
    >
      <Stack p={3} pb={4} gap={3} width="100%" maxWidth={600} mx="auto">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            {t(RESOURCES.Title)}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Grid container spacing={3}>
          {availableFilters.timePeriod && (
            <TimePeriodFilter
              value={timePeriod}
              onChange={onTimePeriodChange}
            />
          )}
          {availableFilters.payPoint && (
            <PayPointFilter value={payPoint} onChange={onPayPointChange} />
          )}
          {availableFilters.timePeriod && timePeriod === "custom" && (
            <DateRangeFilter
              dateTo={dateTo}
              dateFrom={dateFrom}
              onDateFromChange={onDateFromChange}
              onDateToChange={onDateToChange}
            />
          )}
          {availableFilters.employee && (
            <EmployeeFilter value={employee} onChange={onEmployeeChange} />
          )}
          {availableFilters.status && (
            <StatusFilter value={status} onChange={onStatusChange} />
          )}
          {availableFilters.direction && (
            <DirectionFilter value={direction} onChange={onDirectionChange} />
          )}
          {availableFilters.isRefundOrCancellation && (
            <RefundOrCancellationFilter
              value={isRefundOrCancellation ?? false}
              onChange={onRefundOrCancellationChange}
            />
          )}
        </Grid>
        <Box mt={3}>
          <FilterActions
            onApply={onApply}
            onReset={onReset}
            isApplyDisabled={isApplyDisabled}
          />
        </Box>
      </Stack>
    </Drawer>
  );
};
